/**
 * WeaveIn.ai Platform - Promote / Enroll Dialog
 * Validates readiness and promotes a contact to enrolled member
 */

import React, { useState, useEffect } from 'react';
import { X, ArrowUpRight, CheckCircle, AlertCircle, Loader2, UserCheck } from 'lucide-react';
import { contactsAPI, Contact, ContactProgramStatus } from '@/services/api/contacts';

interface PromoteEnrollDialogProps {
  contact: Contact;
  programStatus: ContactProgramStatus;
  onClose: () => void;
  onSuccess?: (enrollmentId: number) => void;
}

interface ReadinessCheck {
  ready: boolean;
  missing_fields?: string[];
  missing_identifiers?: string[];
  missing_forms?: number[];
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'First Name',
  last_name: 'Last Name',
  phone_no: 'Phone Number',
  phone_number: 'Phone Number',
  email: 'Email',
  dob: 'Date of Birth',
  date_of_birth: 'Date of Birth',
  gender: 'Gender',
  race: 'Race',
  ethnicity: 'Ethnicity',
  language: 'Language',
  street_address: 'Street Address',
  city: 'City',
  state: 'State',
  zip_code: 'Zip Code',
  medicaid_id: 'Medicaid ID',
  medicare_id: 'Medicare ID',
};

export default function PromoteEnrollDialog({ contact, programStatus, onClose, onSuccess }: PromoteEnrollDialogProps) {
  const [readiness, setReadiness] = useState<ReadinessCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // The readiness check happens server-side during enrollment
    // We'll simulate by checking the status config requirements
    const checkReadiness = async () => {
      try {
        const configs = await contactsAPI.getStatusConfig(programStatus.program_id);
        const enrollConfig = configs.find((c) => c.promotes_to_enrollment);

        if (!enrollConfig) {
          setReadiness({ ready: true });
          setLoading(false);
          return;
        }

        // Check which required fields are missing on the contact
        const contactFields: Record<string, any> = {
          first_name: contact.first_name,
          last_name: contact.last_name,
          phone_no: contact.phone_no,
          phone_number: contact.phone_no,
          email: contact.email,
          dob: contact.date_of_birth,
          date_of_birth: contact.date_of_birth,
          gender: contact.gender,
          race: contact.race,
          ethnicity: contact.ethnicity,
          language: contact.language,
          street_address: contact.street_address,
          city: contact.city,
          state: contact.state,
          zip_code: contact.zip_code,
        };

        const missingFields = (enrollConfig.required_member_fields || []).filter(
          (f) => !contactFields[f]
        );

        // We can't easily check identifiers client-side, so let the backend handle that
        setReadiness({
          ready: missingFields.length === 0,
          missing_fields: missingFields,
          missing_identifiers: [],
        });
      } catch {
        setReadiness({ ready: true }); // Optimistic if config fetch fails
      } finally {
        setLoading(false);
      }
    };
    checkReadiness();
  }, [contact, programStatus]);

  const handleEnroll = async () => {
    setEnrolling(true);
    setError(null);
    try {
      const result = await contactsAPI.enrollContact(contact.id, programStatus.program_id, {
        assigned_to: programStatus.assigned_to,
      });
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.(result.enrollment_id);
          onClose();
        }, 1000);
      } else {
        // Server-side validation failed
        if (result.details) {
          setReadiness({
            ready: false,
            missing_fields: result.details.missing_fields || [],
            missing_identifiers: result.details.missing_identifiers || [],
            missing_forms: result.details.missing_forms || [],
          });
        }
        setError(result.error || 'Failed to enroll contact');
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.details) {
        setReadiness({
          ready: false,
          missing_fields: detail.details.missing_fields || [],
          missing_identifiers: detail.details.missing_identifiers || [],
          missing_forms: detail.details.missing_forms || [],
        });
        setError('Missing required information for enrollment');
      } else {
        setError(typeof detail === 'string' ? detail : err.message || 'Enrollment failed');
      }
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ArrowUpRight className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Enroll Contact</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Contact summary */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <UserCheck className="w-5 h-5 text-gray-400" />
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {contact.first_name} {contact.last_name || ''}
              </div>
              <div className="text-xs text-gray-500">
                {programStatus.program_name} &mdash; currently "{programStatus.status}"
              </div>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-400 justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking readiness...
            </div>
          )}

          {/* Readiness check */}
          {!loading && readiness && (
            <>
              {readiness.ready ? (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">Ready for enrollment</p>
                    <p className="text-xs text-green-600 dark:text-green-400">All required fields and identifiers are present.</p>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Missing required information</p>
                  </div>
                  {readiness.missing_fields && readiness.missing_fields.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">Missing fields:</p>
                      <div className="flex flex-wrap gap-1">
                        {readiness.missing_fields.map((f) => (
                          <span key={f} className="px-2 py-0.5 bg-amber-100 dark:bg-amber-800/30 text-amber-700 dark:text-amber-300 text-xs rounded">
                            {FIELD_LABELS[f] || f}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {readiness.missing_identifiers && readiness.missing_identifiers.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">Missing identifiers:</p>
                      <div className="flex flex-wrap gap-1">
                        {readiness.missing_identifiers.map((t) => (
                          <span key={t} className="px-2 py-0.5 bg-amber-100 dark:bg-amber-800/30 text-amber-700 dark:text-amber-300 text-xs rounded">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    Update the contact record with the required information before enrolling.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-lg">
              {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm rounded-lg flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Successfully enrolled! Redirecting...
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleEnroll}
            disabled={enrolling || success || (readiness !== null && !readiness.ready)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
            Enroll Member
          </button>
        </div>
      </div>
    </div>
  );
}
